/**
 * Chrome wrapper — the visual frame that surrounds the registration flow.
 *
 * Two display variants:
 *
 *   - "redirect": full-page chrome for register.tourneynation.com.
 *     Renders a top bar with persistent organizer attribution
 *     ("Registering with {organizer name}"), a centered content column,
 *     and the Lockup pinned to the page footer.
 *
 *   - "popup": inside-the-iframe chrome for widgets.tourneynation.com.
 *     No global page background (the modal overlay loader.js opens
 *     handles that), tighter top bar, lockup at the bottom of the
 *     scroll container rather than the viewport.
 *
 * Why a single Chrome with branching: 95% of the chrome is the same
 * (org attribution + lockup + scroll container). Forking into two
 * components doubles the surface area for chrome bugs without
 * meaningful clarity gain.
 */
import type { ReactNode } from 'react';
import type { BrandingResponse } from '@/api/embed';
import type { DisplayMode } from '@/App';
import { OrgLogo } from './OrgLogo';
import './Chrome.css';

interface ChromeProps {
  displayMode: DisplayMode;
  branding: BrandingResponse | null;
  children: ReactNode;
  /** First-party skin preset from config. 'gfg' swaps the attribution bar
   *  for the Golf Fore Good brand header; anything else renders TN chrome. */
  theme?: string;
  /**
   * When set, the header shows a back control (the widget runs in a modal
   * with no browser chrome, so this is the only way back a step). App
   * decides per-route whether a back exists; undefined = no button.
   */
  onBack?: () => void;
}

export function Chrome({ displayMode, branding, children, theme, onBack }: ChromeProps) {
  const isGfg = theme === 'gfg';
  const backButton = onBack ? (
    <button
      type="button"
      className="tn-chrome__back"
      onClick={onBack}
      aria-label="Back"
    >
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none"
           stroke="currentColor" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M11 4l-5 5 5 5" />
      </svg>
    </button>
  ) : null;

  return (
    <div className={`tn-chrome tn-chrome--${displayMode}${isGfg ? ' tn-chrome--gfg' : ''}`}>
      <header className={`tn-chrome__attribution${onBack ? ' tn-chrome__attribution--with-back' : ''}`}>
        {backButton}
        {isGfg ? (
          <>
            {/* GFG brand header. The organizer only appears when the server
                sent a name (a real organization account or an explicit
                public display name) — never a personal name/email. */}
            <span className="tn-chrome__gfg-mark" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                   stroke="#FFFFFF" strokeWidth="2.2"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 21V4" />
                <path d="M7 4l10 3.5L7 11" fill="#FFFFFF" />
              </svg>
            </span>
            <span className="tn-chrome__attribution-label tn-chrome__gfg-label">
              <strong>Golf Fore Good</strong>
              {branding?.organizer_name ? (
                <> · Hosted by <strong>{branding.organizer_name}</strong></>
              ) : null}
            </span>
            {!branding?.organizer_name ? (
              <span className="tn-chrome__gfg-chip">Fundraiser</span>
            ) : null}
          </>
        ) : branding?.organizer_name ? (
          <>
            <OrgLogo branding={branding} className="tn-chrome__logo" />
            <span className="tn-chrome__attribution-label">
              Registering with{' '}
              <strong>{branding.organizer_name}</strong>
            </span>
            {/* Next skin: the organizer's lockup centered in the header row
                (skin-next.css shows it and hides the two above). */}
            <span className="tn-chrome__brand">
              <OrgLogo branding={branding} className="tn-chrome__brand-logo" />
              <span className="tn-chrome__brand-name">{branding.organizer_name}</span>
            </span>
          </>
        ) : (
          <span className="tn-chrome__attribution-label">
            TourneyNation Registration
          </span>
        )}
      </header>
      <main className="tn-chrome__content">{children}</main>
    </div>
  );
}
